import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Modal, Table, Tag, Button, Typography, Space, Select, Descriptions } from "antd"
import type { ColumnsType } from "antd/es/table"
import { RedoOutlined, ThunderboltOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons"
import { logService } from "@/services/logApi"
import dayjs from "dayjs"

const { Text } = Typography

interface UserSrsLogsModalProps {
  open: boolean
  user: any | null
  onClose: () => void
}

export const UserSrsLogsModal: React.FC<UserSrsLogsModalProps> = ({ open, user, onClose }) => {
  const [page, setPage] = useState<number>(1)

  const {
    data: result,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["user_srs_logs", user?.id, page],
    queryFn: () => logService.listSrsLogs({ user_id: user?.id, page, limit: 10 }),
    enabled: open && !!user?.id,
  })

  const getRatingTag = (rating: number) => {
    switch (rating) {
      case 1:
        return <Tag color="error">1 - Again (Học lại)</Tag>
      case 2:
        return <Tag color="warning">2 - Hard (Khó)</Tag>
      case 3:
        return <Tag color="success">3 - Good (Tốt)</Tag>
      case 4:
        return <Tag color="blue">4 - Easy (Dễ)</Tag>
      default:
        return <Tag color="default">Rating {rating}</Tag>
    }
  }

  const columns: ColumnsType<any> = [
    { title: "ID", dataIndex: "id", width: 60, align: "center" },
    {
      title: "Chế độ luyện tập",
      dataIndex: "practice_mode",
      width: 140,
      render: (val) => {
        if (val === "flashcard") return <Tag color="cyan">Flashcard</Tag>
        if (val === "quiz_multiple_choice") return <Tag color="geekblue">Trắc nghiệm</Tag>
        return <Tag color="default">{val}</Tag>
      },
    },
    {
      title: "Đánh giá (FSRS)",
      dataIndex: "rating",
      width: 140,
      align: "center",
      render: (rating) => getRatingTag(rating),
    },
    {
      title: "Kết quả",
      dataIndex: "is_correct",
      width: 110,
      align: "center",
      render: (val) => {
        if (val === true || val === 1) return <Tag color="success" icon={<CheckCircleOutlined />}>Đúng</Tag>
        if (val === false || val === 0) return <Tag color="error" icon={<CloseCircleOutlined />}>Sai</Tag>
        return <Text type="secondary" className="text-xs">Tự đánh giá</Text>
      },
    },
    {
      title: "Thời gian trả lời",
      dataIndex: "response_time_ms",
      width: 120,
      align: "center",
      render: (ms) => (ms ? <span className="font-mono">{`${(ms / 1000).toFixed(1)}s`}</span> : <Text type="secondary">—</Text>),
    },
    {
      title: "Hạn ôn tiếp theo",
      dataIndex: "next_due",
      width: 160,
      align: "center",
      render: (val, record) => (
        <div className="flex flex-col text-xs">
          <span>{val ? dayjs(val).format("DD/MM/YYYY HH:mm") : "-"}</span>
          {record.next_scheduled_days !== undefined && (
            <Text type="secondary" className="text-[11px]">
              (+{record.next_scheduled_days} ngày)
            </Text>
          )}
        </div>
      ),
    },
    {
      title: "Thời gian ôn tập",
      dataIndex: "reviewed_at",
      width: 140,
      align: "center",
      render: (val) => (val ? dayjs(val).format("DD/MM/YYYY HH:mm") : "-"),
    },
  ]

  return (
    <Modal
      title={
        <div className="flex items-center justify-between pr-6">
          <div className="flex items-center gap-2">
            <ThunderboltOutlined className="text-amber-500" />
            <span>Nhật ký học tập (SRS Logs):</span>
            <span className="font-semibold text-gray-800">
              {user?.name || user?.username || user?.email || "Người dùng"}
            </span>
            <Tag color="blue">#{user?.id}</Tag>
          </div>
          <Button size="small" icon={<RedoOutlined />} onClick={() => refetch()} loading={isFetching}>
            Làm mới
          </Button>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="close" type="primary" onClick={onClose}>
          Đóng
        </Button>,
      ]}
      width={1020}
      destroyOnClose
    >
      <div className="py-2">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={result?.data || []}
          loading={isFetching}
          pagination={{
            current: page,
            pageSize: 10,
            total: result?.total || 0,
            onChange: (p) => setPage(p),
            size: "small",
          }}
          size="small"
          scroll={{ x: 950 }}
          expandable={{
            expandedRowRender: (record) => (
              <div className="rounded-md border border-gray-100 bg-gray-50/80 p-3 text-xs leading-relaxed">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="font-semibold text-gray-700">Trạng thái trước (Previous):</span>
                    <ul className="mt-1 list-inside list-disc text-gray-600">
                      <li>State: {record.previous_state ?? "—"}</li>
                      <li>Stability (Độ ổn định): {record.previous_stability ? Number(record.previous_stability).toFixed(2) : "—"}</li>
                      <li>Difficulty (Độ khó): {record.previous_difficulty ? Number(record.previous_difficulty).toFixed(2) : "—"}</li>
                      <li>Scheduled Days: {record.previous_scheduled_days ?? "—"} ngày</li>
                      <li>Due cũ: {record.previous_due ? dayjs(record.previous_due).format("DD/MM/YYYY HH:mm") : "—"}</li>
                    </ul>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700">Trạng thái sau ôn (Next):</span>
                    <ul className="mt-1 list-inside list-disc text-gray-600">
                      <li>State: {record.next_state ?? "—"}</li>
                      <li>Stability (Độ ổn định): {record.next_stability ? Number(record.next_stability).toFixed(2) : "—"}</li>
                      <li>Difficulty (Độ khó): {record.next_difficulty ? Number(record.next_difficulty).toFixed(2) : "—"}</li>
                      <li>Scheduled Days: {record.next_scheduled_days ?? "—"} ngày</li>
                      <li>Due mới: {record.next_due ? dayjs(record.next_due).format("DD/MM/YYYY HH:mm") : "—"}</li>
                    </ul>
                  </div>
                </div>
              </div>
            ),
          }}
        />
      </div>
    </Modal>
  )
}

export default UserSrsLogsModal
